// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — a headless "player" for the balance sim (tests/balance) and the browser test
// hook. It sets the mote's HEIGHT each tick and drives the EXACT shipped core (step), so
// a sim result is the game's result. The load-bearing checks the sim makes:
//   · a TRACKER (follows the ribbon) crushes a FLAT / RANDOM non-reader — reading the
//     ribbon is the skill;
//   · being PRECISE barely helps in Glide (a small penalty for wobble) but is the whole
//     game in Thread (a wobble misses a wall's gap and the run ENDS) — that sign flip is
//     the sudden-death rule being load-bearing (the negative-gap test).

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import { createGame, ribbonAt, step, type GameState } from './game';
import type { Mode } from './modes';

export interface BotPolicy {
  /** Triangular tracking noise half-width (0 = a perfect tracker). */
  sigma: number;
  /** If set, ignore the ribbon and hold this constant height (a non-reader control). */
  flat?: number;
  /** If set, wander at random independent of the ribbon (a non-reader control). */
  random?: boolean;
}

const DT = 1000 / 60;

/** The height the bot flies this tick. Reads the pure ribbon geometry, never game math. */
export function botHeight(s: GameState, p: BotPolicy, rng: Rng, walk: { v: number }): number {
  if (p.flat !== undefined) return p.flat;
  if (p.random) {
    walk.v += (rng() - 0.5) * 0.06;
    walk.v = walk.v < 0 ? 0 : walk.v > 1 ? 1 : walk.v;
    return walk.v;
  }
  // Anticipate one tick: the game scores against the ribbon AFTER it advances the clock.
  const tgt = ribbonAt(s.course, s.clock + DT);
  const noise = (rng() + rng() - 1) * p.sigma; // triangular, mean 0
  const h = tgt + noise;
  return h < 0 ? 0 : h > 1 ? 1 : h;
}

export interface BotResult {
  score: number;
  accuracy: number;
  bestCombo: number;
  died: boolean;
  reachedFrac: number;
  gatesPassed: number;
  gatesTotal: number;
  notesHit: number;
  ticks: number;
}

/** Run one full solo run under a policy + a hand seed. Deterministic. */
export function runBot(mode: Mode, seed: number, p: BotPolicy, handSeed: number): BotResult {
  const s = createGame(mode, seed);
  const rng = makeRng(handSeed);
  const walk = { v: 0.5 };
  let ticks = 0;
  while (!s.over && ticks < 200_000) {
    ticks++;
    step(s, botHeight(s, p, rng, walk), DT);
  }
  const sum = s.summary!;
  return {
    score: sum.score,
    accuracy: sum.accuracy,
    bestCombo: sum.bestCombo,
    died: sum.died,
    reachedFrac: sum.reachedMs / sum.durMs,
    gatesPassed: sum.gatesPassed,
    gatesTotal: sum.gatesTotal,
    notesHit: sum.notesHit,
    ticks,
  };
}

// Handy reference policies for the sim and the browser hook.
export const TRACKER: BotPolicy = { sigma: 0.03 };
export const PRECISE: BotPolicy = { sigma: 0.02 };
export const SLOPPY: BotPolicy = { sigma: 0.13 };
export const FLAT: BotPolicy = { sigma: 0, flat: 0.5 };
export const RANDOM: BotPolicy = { sigma: 0, random: true };
